// Pure helpers for the Superadmin "Embeddings provider" UI. No imports so they run under `deno test`.

/** Size of the DB vector columns. Models returning fewer dimensions are zero-padded to this; more cannot be used. */
export const REQUIRED_DIMENSION = 1536;

export function isSupportedDimension(d: unknown): d is number {
  return typeof d === 'number' && Number.isInteger(d) && d >= 1 && d <= REQUIRED_DIMENSION;
}

export type EmbeddingsPresetKey = 'openai' | 'ollama' | 'custom';

export const EMBEDDINGS_PRESETS: Record<EmbeddingsPresetKey, { label: string; baseUrl: string; model: string; keyless: boolean }> = {
  openai: { label: 'OpenAI text-embedding-3-small', baseUrl: 'https://api.openai.com/v1', model: 'text-embedding-3-small', keyless: false },
  ollama: { label: 'Local (Ollama)', baseUrl: 'http://host.docker.internal:11434/v1', model: 'nomic-embed-text', keyless: true },
  custom: { label: 'Custom OpenAI-compatible', baseUrl: '', model: '', keyless: false },
};

export const OLLAMA_EMBEDDING_MODELS: { id: string; label: string; note: string }[] = [
  { id: 'nomic-embed-text', label: 'nomic-embed-text', note: '768 dims, padded to 1536' },
  { id: 'mxbai-embed-large', label: 'mxbai-embed-large', note: '1024 dims, padded to 1536' },
  { id: 'bge-m3', label: 'bge-m3', note: '1024 dims, padded to 1536, multilingual incl. Arabic' },
];

export const OLLAMA_HELP =
  'No API key needed. localhost / host.docker.internal URLs only work for local setups (the server running the functions must reach that host); ' +
  'the default port is 11434, change it to match where Ollama listens. Run `ollama pull <model>` first.';

const LOCAL_HOSTS = ['localhost', '127.0.0.1', 'host.docker.internal'];

/** True for loopback / host.docker.internal base URLs (the Ollama preset). */
export function isLocalBaseUrl(baseUrl: string): boolean {
  const h = hostOf(baseUrl);
  return !!h && LOCAL_HOSTS.includes(h);
}

export const OPENAI_EMBEDDING_MODELS: { id: string; label: string; note: string }[] = [
  { id: 'text-embedding-3-small', label: 'text-embedding-3-small', note: '1536 dims, recommended' },
  { id: 'text-embedding-ada-002', label: 'text-embedding-ada-002', note: '1536 dims, legacy' },
];

export const CUSTOM_PROVIDER_NOTE =
  'Most chat-only providers (Groq, Anthropic, etc.) have no embeddings API. Use a provider whose embeddings model returns at most 1536 dimensions (smaller vectors are zero-padded to 1536).';

/** Hosts known to offer chat completions but no embeddings API. Extend this list as needed. */
export const CHAT_ONLY_HOSTS: string[] = [
  'api.groq.com', 'api.anthropic.com', 'api.cerebras.ai', 'api.x.ai', 'api.deepseek.com',
];

export const NO_EMBEDDINGS_MESSAGE = 'This provider does not offer embeddings models';

export function hostOf(baseUrl: string): string | null {
  const t = baseUrl.trim();
  if (!t) return null;
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Returns an error message when the base URL host is a known chat-only provider, else null. */
export function chatOnlyHostError(baseUrl: string): string | null {
  const host = hostOf(baseUrl);
  if (!host) return null;
  return CHAT_ONLY_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)) ? NO_EMBEDDINGS_MESSAGE : null;
}

export const MODEL_NOT_FOUND_MESSAGE =
  'This provider has no embeddings model with that name. Check the model name, or use the OpenAI preset.';

export function isModelNotFound(text: string | undefined): boolean {
  return !!text && /model_not_found|does not exist/i.test(text);
}

export const DIMENSION_NOTE =
  `The model must return at most ${REQUIRED_DIMENSION} dimensions. Smaller vectors are zero-padded to ${REQUIRED_DIMENSION} (the document and chunk tables use a ` +
  `vector(${REQUIRED_DIMENSION}) column; padding does not change cosine similarity). Models returning more than ${REQUIRED_DIMENSION} dimensions (e.g. 3072) cannot be used.`;

/** "768 -> padded to 1536" / "1536-dim". */
export function dimensionText(d: number): string {
  return d < REQUIRED_DIMENSION ? `${d} → padded to ${REQUIRED_DIMENSION}` : `${d}-dim`;
}

export const REEMBED_WARNING =
  'Content that was already embedded used a different model, and vectors from different models are not comparable. ' +
  'After this change, existing documents must be re-synced / re-embedded for search and Ask Masira to be accurate. ' +
  'There is no automatic re-embed tool.';

export interface EmbeddingsProvider {
  id: string;
  label: string;
  base_url: string;
  default_model: string;
  api_key_last4: string | null;
  is_active: boolean;
  health_status: 'unknown' | 'healthy' | 'unhealthy';
  health_error: string | null;
  health_checked_at: string | null;
  embedding_dimension: number | null;
}

export interface EmbeddingsTestResult {
  ok: boolean;
  httpStatus?: number;
  latencyMs?: number;
  dimension?: number;
  requiredDimension?: number;
  error?: string;
  errorCode?: string;
  testToken?: string;
}

/** What the test was run against. The apiKey is only held in memory to detect edits. */
export interface TestedConfig {
  baseUrl: string;
  model: string;
  apiKey: string;
}

export function sameConfig(a: TestedConfig, b: TestedConfig): boolean {
  return a.baseUrl.trim() === b.baseUrl.trim() && a.model.trim() === b.model.trim() && a.apiKey === b.apiKey;
}

/** A test token is usable only for the exact base URL / model / key it was issued for. */
export function hasValidTest(
  test: { result: EmbeddingsTestResult; config: TestedConfig } | null,
  current: TestedConfig,
): boolean {
  return !!test && test.result.ok === true && !!test.result.testToken
    && isSupportedDimension(test.result.dimension) && sameConfig(test.config, current);
}

export function canSave(opts: {
  label: string; test: { result: EmbeddingsTestResult; config: TestedConfig } | null;
  current: TestedConfig; saving: boolean; testing: boolean; isEdit?: boolean; storedBaseUrl?: string; keyless?: boolean; storedHasKey?: boolean;
}): boolean {
  if (chatOnlyHostError(opts.current.baseUrl)) return false;
  if (requiresKeyReentry({ keyless: opts.keyless, isEdit: !!opts.isEdit, baseUrl: opts.current.baseUrl, storedBaseUrl: opts.storedBaseUrl, apiKey: opts.current.apiKey, storedHasKey: opts.storedHasKey })) return false;
  return !opts.saving && !opts.testing && opts.label.trim().length > 0 && hasValidTest(opts.test, opts.current);
}

export const KEY_REENTRY_HELP = 'Re-enter the API key to use a different base URL';

/** In edit mode, pointing the stored key at a different host requires typing a new key. */
export function requiresKeyReentry(opts: { isEdit: boolean; baseUrl: string; storedBaseUrl?: string; apiKey: string; keyless?: boolean; storedHasKey?: boolean }): boolean {
  // Keyless presets bypass only when the stored row has no key; a stored key must not follow a changed base URL.
  if (opts.keyless && !opts.storedHasKey) return false;
  if (!opts.isEdit || opts.storedBaseUrl === undefined) return false;
  return opts.baseUrl.trim() !== opts.storedBaseUrl.trim() && opts.apiKey.length === 0;
}

export function canTest(opts: { baseUrl: string; model: string; apiKey: string; isEdit: boolean; storedBaseUrl?: string; keyless?: boolean; storedHasKey?: boolean }): boolean {
  if (!opts.baseUrl.trim() || !opts.model.trim()) return false;
  if (chatOnlyHostError(opts.baseUrl)) return false;
  if (requiresKeyReentry(opts)) return false;
  return opts.isEdit || !!opts.keyless || opts.apiKey.length > 0;
}

/** Human-readable outcome of a test, for display next to the Test button. */
export function describeTest(r: EmbeddingsTestResult): { ok: boolean; title: string; detail?: string; raw?: string } {
  if (r.ok) {
    const d = r.dimension ?? REQUIRED_DIMENSION;
    const padded = d < REQUIRED_DIMENSION;
    return {
      ok: true,
      title: padded ? `Passed: ${d}-dimension vectors, zero-padded to ${REQUIRED_DIMENSION}` : `Passed: ${d}-dimension vectors`,
      detail: [padded ? 'Padding with zeros does not change cosine similarity.' : null, r.latencyMs != null ? `${r.latencyMs} ms` : null].filter(Boolean).join(' · ') || undefined,
    };
  }
  if (isModelNotFound(r.error)) {
    return { ok: false, title: MODEL_NOT_FOUND_MESSAGE, raw: r.error };
  }
  switch (r.errorCode) {
    case 'dimension_mismatch':
      return {
        ok: false,
        title: `Too many dimensions: the model returned ${r.dimension ?? 'a different number of'}-dimension vectors`,
        detail: `The model must return at most ${REQUIRED_DIMENSION} dimensions (smaller vectors are zero-padded to ${REQUIRED_DIMENSION}). Models that return more, such as text-embedding-3-large (3072), cannot be used.`,
      };
    case 'http_error':
      if (r.httpStatus === 401 || r.httpStatus === 403) {
        return { ok: false, title: 'Authentication failed', detail: `The provider rejected the API key (HTTP ${r.httpStatus}). Check the key and that it can use embeddings.` };
      }
      if (r.httpStatus === 404) {
        return { ok: false, title: 'Endpoint or model not found', detail: 'Check the base URL (it should end in /v1) and the model name.', raw: r.error };
      }
      if (r.httpStatus === 429) {
        return { ok: false, title: 'Rate limited or out of quota', detail: 'The provider returned HTTP 429. Try again shortly or check the account quota.' };
      }
      return { ok: false, title: `Provider returned HTTP ${r.httpStatus ?? 'error'}`, raw: r.error };
    case 'unreachable':
      return { ok: false, title: 'Could not reach the provider', detail: 'Network error: check the base URL and that the endpoint is reachable from the server.' };
    case 'bad_response':
      return { ok: false, title: 'Unexpected response', detail: 'The endpoint did not return an OpenAI-style embeddings response.' };
    default:
      return { ok: false, title: 'Test failed', raw: r.error };
  }
}

/** Message for an error returned by save / activate / deactivate / delete. */
export function describeApiError(code: string | undefined, message: string | undefined): string {
  switch (code) {
    case 'test_required': return 'The connection test is missing or out of date. Run Test again for the current URL, model and key, then save.';
    case 'not_tested': return 'This provider has not passed the embeddings test. Edit it and run Test first.';
    case 'key_reentry_required': return KEY_REENTRY_HELP;
    case 'kind_mismatch': return 'That provider is not an embeddings provider.';
    case 'embeddings_platform_only': return 'Embeddings providers are platform-wide only.';
    default: return message && message.length > 0 ? message : 'Something went wrong. Please try again.';
  }
}

export function canActivate(p: EmbeddingsProvider): boolean {
  return !p.is_active && p.health_status === 'healthy' && isSupportedDimension(p.embedding_dimension);
}

export function activateBlockedReason(p: EmbeddingsProvider): string | null {
  if (p.is_active) return 'Already active';
  if (canActivate(p)) return null;
  return 'Run Test first: activation requires a passing embeddings test (at most 1536 dimensions)';
}

export function canDelete(p: EmbeddingsProvider): boolean {
  return !p.is_active;
}

export function deleteBlockedReason(p: EmbeddingsProvider): string | null {
  return p.is_active ? 'The active provider cannot be deleted. Deactivate it first (embeddings will fall back to environment variables).' : null;
}

export function healthText(p: EmbeddingsProvider): string {
  if (p.health_status === 'healthy') {
    return p.embedding_dimension != null ? `healthy · ${dimensionText(p.embedding_dimension)}` : 'healthy';
  }
  if (p.health_status === 'unhealthy') {
    return p.embedding_dimension != null && p.embedding_dimension !== REQUIRED_DIMENSION
      ? `unhealthy · ${p.embedding_dimension}-dim` : 'unhealthy';
  }
  return 'not tested';
}

export interface EmbeddingsViewState {
  active: EmbeddingsProvider | null;
  usingEnvFallback: boolean;
  banner: string | null;
}

export function deriveViewState(providers: EmbeddingsProvider[]): EmbeddingsViewState {
  const active = providers.find((p) => p.is_active) ?? null;
  if (active) return { active, usingEnvFallback: false, banner: null };
  return {
    active: null,
    usingEnvFallback: true,
    banner: 'No embeddings provider is active. Embeddings fall back to the EMBEDDINGS_BASE_URL / EMBEDDINGS_API_KEY / EMBEDDINGS_MODEL environment variables ' +
      '(or LLM_API_KEY). The UI cannot see whether those are set; if they are not, search and document ingestion will fail to embed.',
  };
}
